import * as React from 'react';
import { Component } from 'react';
import { observer } from 'mobx-react';
import { inject, orderBy, sortValues } from '../../utils';
import { action, computed, observable } from 'mobx';
import { translate } from '../Translate';
import {
    FilterValue,
    FilterSearchKeys,
    FilterProps,
} from '../../models/components/Filter';
import { DataStore, FilterStore, TranslationStore } from '../../stores';
import { Filter } from '../../models/configuration/filter/Filter';
import { DataSort } from '../../models/configuration/DataSort';

interface WithFilterProps {
    dataStore?: DataStore;
    filterStore?: FilterStore;
    translationStore?: TranslationStore;
    filter: FilterProps;
}

// Higher-order component to return a filter with data and methods required to function
const withFilter = WrappedComponent => {
    @inject('translationStore')
    @inject('dataStore')
    @inject('filterStore')
    @observer
    class FilterItem extends Component<WithFilterProps> {
        @observable search: string = '';
        @observable expanded: boolean = this.props.dataStore.config.filtersTop
            ? false
            : this.props.filter.hasOwnProperty('expanded')
            ? this.props.filter.expanded
            : true;
        @observable clearIndividualFilters: boolean = this.props.dataStore
            .config.clearIndividualFilters;

        @computed
        get filterId(): string {
            return this.props.filter.label.toLowerCase().replace(/\s/g, '-');
        }

        @computed
        get filterSearchKey(): FilterSearchKeys {
            return this.props.filter.filterSearchKey;
        }

        @computed
        get values(): Array<FilterValue> {
            return this.props.filter.values || [];
        }

        @computed
        get filterOptions() {
            const { dataStore } = this.props;
            const { config } = dataStore;
            return config.filterOptions;
        }

        @computed
        get filterConfig(): Filter {
            const { dataStore, filter } = this.props;
            const { config } = dataStore;
            return config.filtersConfig.find(
                item => item.property === filter.property
            );
        }

        @computed
        get filteredValues(): Array<FilterValue> {
            const { property, filterSearchKey } = this.props.filter;
            const values = this.values
                .filter(
                    value =>
                        value.label
                            .toLowerCase()
                            .includes(this.search.toLowerCase()) &&
                        !this.props.filterStore.isChecked(
                            property,
                            value.value,
                            filterSearchKey
                        )
                )
                .map(value => {
                    const isActive = this.props.filterStore.isActive(
                        property,
                        value.value
                    );
                    return {
                        isActive,
                        label: value.label,
                        value: value.value,
                    };
                });
            return this.sortFilter(values);
        }

        @computed
        get filteredCount(): number {
            return this.filteredValues.length + this.selectedValues.length;
        }

        @computed
        get selectedValues(): Array<FilterValue> {
            const values = this.values.filter(value =>
                this.props.filterStore.isChecked(
                    this.props.filter.property,
                    value.value,
                    this.props.filter.filterSearchKey
                )
            );

            return this.sortFilter(values);
        }

        @computed
        get filterDropdownText(): string {
            const { translationStore } = this.props;
            return this.selectedValues.length
                ? this.selectedValues
                      .map(value => translate(translationStore, value.label))
                      .join(', ')
                : translate(translationStore, 'Select filters');
        }

        @computed
        get enableSearch(): boolean {
            return this.props.filter.enableSearch ? true : false;
        }

        @action
        toggleExpanded = event => {
            event.preventDefault();
            this.expanded = !this.expanded;
        };

        @action
        filterSearch = (value: string) => {
            this.search = value;
        };

        @action
        clearSearch = () => {
            this.search = '';
        };

        @action
        sortFilter = (values: Array<FilterValue>) => {
            if (values && this.filterConfig) {
                const {
                    sortActiveFirst: allSortActiveFirst = false,
                } = this.filterOptions;
                const {
                    sort = {
                        type: 'string',
                        key: 'label',
                        direction: 'ASC',
                    },
                    sortActiveFirst = allSortActiveFirst,
                } = this.filterConfig;
                if (sortActiveFirst) {
                    const sortConfig: Array<DataSort> = [
                        {
                            type: 'number',
                            key: 'isActive',
                            direction: 'DESC',
                        },
                        sort,
                    ];
                    values = orderBy(values, sortConfig);
                } else {
                    values = sortValues(values, sort);
                }
            }
            return values;
        };

        @computed
        get isRequired(): boolean {
            const { filter, filterStore } = this.props;
            return (filterStore as FilterStore).isFilterRequired(
                filter.property
            );
        }

        @computed
        get disclaimers() {
            return this.props.filter.disclaimers;
        }

        render() {
            return (
                <WrappedComponent
                    expanded={this.expanded}
                    toggleExpanded={this.toggleExpanded}
                    selectedCount={this.selectedValues.length}
                    total={this.values.length}
                    search={this.search}
                    clearSearch={this.clearSearch}
                    selectedValues={this.selectedValues}
                    filteredValues={this.filteredValues}
                    filterSearch={this.filterSearch}
                    enableSearch={this.enableSearch}
                    filterDropdownText={this.filterDropdownText}
                    isRequired={this.isRequired}
                    filterId={this.filterId}
                    disclaimers={this.disclaimers}
                    {...this.props}
                />
            );
        }
    }

    return FilterItem;
};

export default withFilter;



// WEBPACK FOOTER //
// ./src/Components/Filters/withFilter.tsx